// frontend/src/components/dialogs/SegmentShortcutSelector.jsx
// Component for selecting shortcut segments to populate GPS data from completed trips.
// Shortcuts are non-sequential waypoint pairs defined in route_segments.

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
    Check, Info, AlertCircle, AlertTriangle,
    Loader2, Clock, MapPin, Split, Route
} from 'lucide-react';
import { toast } from 'react-toastify';
import segmentMatrixService from '../../services/segmentMatrixService';
import ConfirmDialogShell from './ConfirmDialogShell';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import {
    buildSegmentKey,
    formatDistanceMeters,
    formatDurationSeconds
} from '../../utils/segmentUtils';
import { getZeroDistanceLabel } from '../../utils/distanceUtils';

const getPositiveDurationSeconds = (value) => {
    const seconds = Number(value);
    return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null;
};

const getShortcutDurationSeconds = (shortcut) => (
    getPositiveDurationSeconds(shortcut.reference_duration_seconds)
    ?? getPositiveDurationSeconds(shortcut.refDuration)
    ?? getPositiveDurationSeconds(shortcut.ors_duration_seconds)
);

/**
 * Single shortcut row component
 */
const ShortcutRow = ({
    shortcut,
    isSelected,
    onToggle,
    existingGpsData,
    disabled,
    t,
    cn,
    text,
    border,
    distanceUnit,
    durationLanguage
}) => {
    const hasExistingData = existingGpsData?.pointCount > 0;
    const zeroDistanceLabel = getZeroDistanceLabel(distanceUnit, t);
    const shortcutDurationSeconds = getShortcutDurationSeconds(shortcut);

    return (
        <div className={cn(
            "border rounded-lg transition-all duration-200",
            border.default,
            isSelected ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20' : ''
        )}>
            {/* Row content */}
            <div
                className={cn(
                    "flex items-center gap-3 p-3 cursor-pointer",
                    "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                )}
                onClick={() => !disabled && onToggle()}
            >
                {/* Checkbox — click is handled by parent row div, so stop propagation to avoid double-toggle */}
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 pointer-events-none"
                />

                {/* Shortcut icon */}
                <Split className="w-4 h-4 text-purple-500 flex-shrink-0" />

                {/* Segment info */}
                <div className="flex-1 min-w-0">
                    <div className={cn("font-medium flex items-center gap-2 flex-wrap", text.primary)}>
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span title={shortcut.from_waypoint_name}>{shortcut.from_waypoint_name}</span>
                        <span className="text-gray-400">→</span>
                        <span title={shortcut.to_waypoint_name}>{shortcut.to_waypoint_name}</span>
                    </div>

                    {/* Existing data status */}
                    <div className={cn("flex items-center gap-2 mt-1 text-sm", text.secondary)}>
                        {hasExistingData ? (
                            <>
                                <Check className="w-3 h-3 text-green-500" />
                                <span className="text-green-600 dark:text-green-400">
                                    {t('hasGpsData')} ({existingGpsData.pointCount} {t('points')})
                                </span>
                            </>
                        ) : (
                            <>
                                <Route className="w-3 h-3 text-gray-400" />
                                <span>{t('noGpsData')}</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Metrics from route_segments */}
                <div className="flex items-center gap-4 text-sm">
                    {shortcutDurationSeconds != null && (
                        <div className={cn("flex items-center gap-1", text.secondary)}>
                            <Clock className="w-3 h-3" />
                            <span>{formatDurationSeconds(shortcutDurationSeconds, t, { language: durationLanguage })}</span>
                        </div>
                    )}
                    {shortcut.distance_meters != null && (
                        <div className={cn("flex items-center gap-1", text.secondary)}>
                            <span>{formatDistanceMeters(shortcut.distance_meters, distanceUnit, t, { compact: false, zeroLabel: zeroDistanceLabel })}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Main component for shortcut segment selection
 */
export const SegmentShortcutSelector = ({
    tripId,
    routeId,
    entityId,
    referenceType,
    shortcutSegments = [],
    onSave,
    onCancel,
    loading: externalLoading = false
}) => {
    const { t, i18n } = useTranslation();
    const { text, border, components, semantic, cn } = useTheme();
    const { distanceUnit } = useDateTimePreferences();
    const durationLanguage = i18n?.resolvedLanguage || i18n?.language;

    // State
    const [existingGpsData, setExistingGpsData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Selection state: Set of shortcut keys
    const [selectedShortcuts, setSelectedShortcuts] = useState(new Set());
    const [pendingSaveSegments, setPendingSaveSegments] = useState(null);

    const fetchExistingGpsData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Build edges array for batch query
            const edges = shortcutSegments.map(s => ({
                fromWaypointId: s.from_waypoint_id,
                toWaypointId: s.to_waypoint_id
            }));

            const response = await segmentMatrixService.getExistingShortcuts(
                entityId,
                routeId,
                edges
            );

            if (response.success) {
                // Convert to a map for easy lookup
                const dataMap = {};
                for (const [key, value] of Object.entries(response.existingShortcuts || {})) {
                    // value is an array of { archived_type, point_count, recorded_at }
                    const repData = value.find(v => v.archived_type === referenceType);
                    if (repData) {
                        dataMap[key] = { pointCount: repData.point_count };
                    }
                }
                setExistingGpsData(dataMap);

                // Pre-select shortcuts that don't have GPS data yet
                const initialSelections = new Set();
                for (const shortcut of shortcutSegments) {
                    const key = buildSegmentKey(shortcut.from_waypoint_id, shortcut.to_waypoint_id);
                    if (!dataMap[key]?.pointCount) {
                        initialSelections.add(key);
                    }
                }
                setSelectedShortcuts(initialSelections);
            }
        } catch (err) {
            console.error('Error fetching existing GPS data:', err);
            setError(err.response?.data?.error || t('failedToLoadShortcuts'));
        } finally {
            setIsLoading(false);
        }
    }, [entityId, referenceType, routeId, shortcutSegments, t]);

    // Fetch existing GPS data for shortcuts on mount
    useEffect(() => {
        if (shortcutSegments.length > 0) {
            fetchExistingGpsData();
        } else {
            setIsLoading(false);
        }
    }, [fetchExistingGpsData, shortcutSegments]);

    const handleToggleShortcut = (shortcutKey) => {
        setSelectedShortcuts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(shortcutKey)) {
                newSet.delete(shortcutKey);
            } else {
                newSet.add(shortcutKey);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const allKeys = shortcutSegments.map(s => buildSegmentKey(s.from_waypoint_id, s.to_waypoint_id));
        setSelectedShortcuts(new Set(allKeys));
    };

    const handleSelectNone = () => {
        setSelectedShortcuts(new Set());
    };

    const buildSelectedSegments = () => (
        shortcutSegments
            .filter(s => selectedShortcuts.has(buildSegmentKey(s.from_waypoint_id, s.to_waypoint_id)))
            .map(s => ({
                fromWaypointId: s.from_waypoint_id,
                toWaypointId: s.to_waypoint_id
                // No variant = shortcut (not a named variant)
            }))
    );

    const getReplacementCount = () => (
        Array.from(selectedShortcuts).filter(key => existingGpsData[key]?.pointCount > 0).length
    );

    const handleSaveClick = () => {
        if (selectedShortcuts.size === 0) {
            toast.error(t('selectAtLeastOneShortcut'));
            return;
        }

        setPendingSaveSegments(buildSelectedSegments());
    };

    const handleSave = async () => {
        if (!pendingSaveSegments?.length) {
            return;
        }

        try {
            setIsSaving(true);

            // Use unified endpoint for saving reference GPS data
            const response = await segmentMatrixService.saveReferenceSegments(
                entityId,
                tripId,
                referenceType,
                pendingSaveSegments
            );

            if (response.success) {
                const savedCount = response.saved?.length || 0;
                toast.success(
                    t('shortcutSegmentsSaved', { count: savedCount })
                );

                if (onSave) {
                    onSave(response);
                }
            }
        } catch (err) {
            console.error('Error saving shortcut segments:', err);
            toast.error(err.response?.data?.error || t('failedToSaveShortcuts'));
        } finally {
            setPendingSaveSegments(null);
            setIsSaving(false);
        }
    };

    const selectedCount = selectedShortcuts.size;
    const isDisabled = externalLoading || isSaving;
    const replacementCount = getReplacementCount();

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                <p className={cn("mt-3 text-sm", text.secondary)}>
                    {t('loadingShortcuts')}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cn("p-4 rounded-lg", semantic.error.background, semantic.error.text)}>
                <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    if (shortcutSegments.length === 0) {
        return (
            <div className={cn("p-4 rounded-lg text-center", semantic.info.background, semantic.info.text)}>
                <Info className="w-8 h-8 mx-auto mb-2" />
                <p>{t('noShortcutsFound')}</p>
                <p className={cn("mt-2 text-sm", text.secondary)}>
                    {t('shortcutsDefinedInRouteConfig')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Instructions */}
            <div className={cn("p-3 rounded-lg flex items-start gap-2", semantic.info.background, semantic.info.text)}>
                <Split className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                    <p className="font-medium mb-1">
                        {t('addShortcutSegmentsFor', { type: referenceType })}
                    </p>
                    <p>
                        {t('shortcutSegmentInstructions')}
                    </p>
                </div>
            </div>

            {/* Select all / none buttons */}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={isDisabled}
                    className={cn("text-sm px-2 py-1 rounded", text.link, "hover:underline")}
                >
                    {t('selectAll')}
                </button>
                <span className={text.secondary}>|</span>
                <button
                    type="button"
                    onClick={handleSelectNone}
                    disabled={isDisabled}
                    className={cn("text-sm px-2 py-1 rounded", text.link, "hover:underline")}
                >
                    {t('selectNone')}
                </button>
            </div>

            {/* Shortcut list */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
                {shortcutSegments.map((shortcut) => {
                    const key = buildSegmentKey(shortcut.from_waypoint_id, shortcut.to_waypoint_id);
                    const isSelected = selectedShortcuts.has(key);
                    const gpsData = existingGpsData[key];

                    return (
                        <ShortcutRow
                            key={key}
                            shortcut={shortcut}
                            isSelected={isSelected}
                            onToggle={() => handleToggleShortcut(key)}
                            existingGpsData={gpsData}
                            disabled={isDisabled}
                            t={t}
                            cn={cn}
                            text={text}
                            border={border}
                            distanceUnit={distanceUnit}
                            durationLanguage={durationLanguage}
                        />
                    );
                })}
            </div>

            {/* Footer with action buttons */}
            <div className={cn("flex items-center justify-between pt-4 border-t", border.default)}>
                <div className={cn("text-sm", text.secondary)}>
                    {t('shortcutsSelected', { count: selectedCount })}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isDisabled}
                        className={cn(
                            "px-4 py-2 rounded",
                            isDisabled ? components.button.disabled : components.button.secondary
                        )}
                    >
                        {t('back')}
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={isDisabled || selectedCount === 0}
                        className={cn(
                            "px-4 py-2 rounded flex items-center gap-2",
                            isDisabled || selectedCount === 0
                                ? components.button.disabled
                                : "bg-purple-600 hover:bg-purple-700 text-white"
                        )}
                    >
                        {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {t('saveShortcuts')}
                    </button>
                </div>
            </div>

            <ConfirmDialogShell
                isOpen={!!pendingSaveSegments}
                onClose={() => !isSaving && setPendingSaveSegments(null)}
                onConfirm={handleSave}
                title={t('confirmSaveShortcutSegments') || 'Confirm Shortcut Segment Save'}
                confirmLabel={t('saveShortcuts')}
                confirmVariant={replacementCount > 0 ? 'warning' : 'primary'}
                confirmIcon={replacementCount > 0 ? AlertTriangle : Check}
                isLoading={isSaving}
                isDisabled={isSaving}
                showIcon={replacementCount > 0}
                iconVariant="warning"
                closeOnBackdropClick={!isSaving}
            >
                <div className="space-y-3">
                    <p className={cn("text-sm", text.secondary)}>
                        {t('confirmSaveShortcutSegmentsMessage', {
                            count: pendingSaveSegments?.length || 0,
                            type: referenceType
                        }) || `Save ${pendingSaveSegments?.length || 0} shortcut segment(s) to ${referenceType}?`}
                    </p>
                    {replacementCount > 0 && (
                        <div className={cn("p-3 rounded-lg text-sm", semantic.warning.background, semantic.warning.text)}>
                            {t('shortcutSegmentsReplaceWarning', { count: replacementCount }) ||
                                `${replacementCount} selected shortcut segment(s) already have GPS data and will be replaced.`}
                        </div>
                    )}
                </div>
            </ConfirmDialogShell>
        </div>
    );
};

export default SegmentShortcutSelector;
